import React, { FC, lazy } from "react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
} from "react-router-dom";
import RootLayout from "components/common/ui/RootLayout";
import Routes from "models/routes";
import IngredientListPage from "components/food/ui/ingredientList/IngredientListPage";
import AuthGuard from "components/login/ui/AuthGuard";
import AuthProvider from "components/login/ui/AuthProvider";
import MenuEditPage from "components/menu/ui/MenuEditPage";
import RecipeListPage from "components/food/ui/recipeList/recipeListPage";
import MenuListPage from "components/menu/ui/menuList/menuListPage";
import MenuCalendarPage from "components/menu/ui/menuPublishment/menuCalendarPage";
import PromptSnackBarProvider from "components/common/ui/PromptSnackBarProvider";
import IngredientStockListPage from "components/inventory/ui/stockLevelList/IngredientStockListPage";
import FoodStockDetailPage from "components/inventory/ui/stockDetails/FoodStockDetailPage";
import FoodPurchasingPage from "components/inventory/ui/purchasing/FoodPurchasingPage";
import ReplenishBySupplierOrderCSVPage from "components/inventory/ui/replenishment/ReplenishBySupplierOrderCSVPage";
import MenuLoopEditPage from "components/menu/ui/menuLoop/MenuLoopEditPage";
import MenuLoopListPage from "components/menu/ui/menuLoopList/menuLoopListPage";
// eslint-disable-next-line max-len
import InvoiceUpdateSupplierInfoPage from "components/food/ui/supplierInfo/invoiceUpdateSupplier/InvoiceUpdateSupplierInfoPage";
import CycleFoodPurchasingPage from "components/inventory/ui/cyclePurchasing/CycleFoodPurchasingPage";
import LoginContainer from "./login/ui/LoginContainer";
import ForgotPasswordPage from "./login/ui/ForgotPasswordPage";
import ResetPasswordPage from "./login/ui/ResetPasswordPage";
import RecipeContainer from "./food/ui/RecipeContainer";
import DefaultErrorPage from "./error/DefaultError";
import MyAccountPage from "./user/ui/myAccount/MyAccountPage";
import NutritionContainer from "./food/ui/NutritionContainer";

const IngredientMultiSupplierEditPage = lazy(
  () =>
    import(
      /* webpackChunkName: "IngredientMultiSupplierEditPage" */
      "components/food/ui/ingredientMultiSupplierEdit/IngredientMultiSupplierEditPage"
    ),
);

const RecipeEditPage = lazy(
  () =>
    import(
      /* webpackChunkName: "RecipeMultiVariantEditPage" */
      "components/food/ui/recipeMultiVariantEdit/RecipeMultiVariantEditPage"
    ),
);

const FoodGroupListPage = lazy(
  () =>
    import(
      /* webpackChunkName: "FoodGroupListPage" */
      "components/food/ui/foodGroupList/FoodGroupListPage"
    ),
);

const TagBoardPage = lazy(
  () =>
    import(
      /* webpackChunkName: "TagBoardPage" */
      "components/food/ui/tagBoard/TagBoardPage"
    ),
);

const SupplyProfileListPage = lazy(
  () =>
    import(
      /* webpackChunkName: "SupplyProfileListPage" */
      "components/inventory/ui/supplyProfile/SupplyProfileListPage"
    ),
);

const SupplyProfileBoardPage = lazy(
  () =>
    import(
      /* webpackChunkName: "SupplyProfileBoardPage" */
      "components/inventory/ui/supplyProfile/board/SupplyProfileBoardPage"
    ),
);

const RecipeViewPage = lazy(
  () =>
    import(
      /* webpackChunkName: "RecipeViewPage" */
      "components/food/ui/recipeView/RecipeViewPage"
    ),
);

interface AppRouterProps {}

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path="/" errorElement={<DefaultErrorPage />}>
      <Route path={Routes.LOGIN} element={<LoginContainer />} />
      <Route path={Routes.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
      <Route path={Routes.RESET_PASSWORD} element={<ResetPasswordPage />} />
      <Route
        element={
          <AuthGuard>
            <RootLayout />
          </AuthGuard>
        }
      >
        <Route path={Routes.HOME} element={<h1>Hello</h1>} />
        <Route path={Routes.COMING_SOON} element={<h1>Coming soon ... </h1>} />
        <Route path={Routes.FOOD} element={<IngredientListPage />} />
        <Route path={Routes.FOOD_INGREDIENT} element={<IngredientListPage />} />
        <Route
          path={`${Routes.FOOD_INGREDIENT_EDIT}/:foodId`}
          element={<IngredientMultiSupplierEditPage />}
        />
        <Route
          path={Routes.FOOD_INGREDIENT_CREATE}
          element={<IngredientMultiSupplierEditPage />}
        />
        <Route
          path={Routes.FOOD_UPDATE_SUPPLIER_INFO_BY_INVOICE}
          element={<InvoiceUpdateSupplierInfoPage />}
        />
        <Route path={Routes.FOOD_RECIPE} element={<RecipeListPage />} />
        <Route path={Routes.FOOD_GROUP} element={<FoodGroupListPage />} />
        <Route path={Routes.FOOD_TAG_BOARD} element={<TagBoardPage />} />
        <Route
          path={`${Routes.FOOD_RECIPE_EDIT}/:foodId/:recipeId`}
          element={<RecipeEditPage />}
        />
        <Route path={Routes.FOOD_RECIPE_CREATE} element={<RecipeEditPage />} />
        <Route
          path={`${Routes.FOOD_RECIPE_VIEW}/:foodId`}
          element={<RecipeViewPage />}
        />
        <Route path={Routes.MENU} element={<MenuListPage />} />
        <Route path={Routes.MENU_CALENDAR} element={<MenuCalendarPage />} />
        <Route
          path={`${Routes.MENU_EDIT}/:menuId`}
          element={<MenuEditPage />}
        />
        <Route path={Routes.MENU_CREATE} element={<MenuEditPage />} />
        <Route path={Routes.MENU_LOOP} element={<MenuLoopListPage />} />
        <Route
          path={`${Routes.MENU_LOOP_CREATE}`}
          element={<MenuLoopEditPage />}
        />
        <Route
          path={`${Routes.MENU_LOOP_EDIT}/:menuLoopId`}
          element={<MenuLoopEditPage />}
        />
        <Route path={Routes.INVENTORY} element={<IngredientStockListPage />} />
        <Route
          path={`${Routes.INVENTORY_DETAILS}/:foodId`}
          element={<FoodStockDetailPage />}
        />
        <Route
          path={Routes.INVENTORY_EDIT_CSV}
          element={<ReplenishBySupplierOrderCSVPage />}
        />
        <Route
          path={Routes.INVENTORY_PURCHASING_CYCLE}
          element={<CycleFoodPurchasingPage />}
        />
        <Route
          path={Routes.INVENTORY_PURCHASING}
          element={<FoodPurchasingPage />}
        />
        <Route
          path={Routes.INVENTORY_SUPPLY_PROFILE}
          element={<SupplyProfileListPage />}
        />
        <Route
          path={Routes.INVENTORY_SUPPLY_PROFILE_BOARD}
          element={<SupplyProfileBoardPage />}
        />
        <Route path={Routes.NUTRITION} element={<NutritionContainer />} />
        <Route path={Routes.RECIPE_GRAPH} element={<RecipeContainer />} />
        <Route path={Routes.USER_MY_ACCOUNT} element={<MyAccountPage />} />
      </Route>
    </Route>,
  ),
);

const AppRouter: FC<AppRouterProps> = () => {
  return (
    <PromptSnackBarProvider>
      <AuthProvider>
        <RouterProvider router={router} />
      </AuthProvider>
    </PromptSnackBarProvider>
  );
};

export default AppRouter;
